import {ChangeDetectorRef, Component, Input, OnInit, ViewChild} from '@angular/core';
import {ApolloQueryObservable} from 'apollo-angular';
import {SessionActions} from '../../../../shared/actions';
import {ActivatedRoute} from '@angular/router';
import {FileService} from '../../../../core/visbos-core/http/file.service';
import {GetInvoiceQuery} from '../../../../shared/graphql/graphql.types';
import {InvoiceService} from '../../../services/invoice/invoice.service';
import {GenericModalComponent} from '../../../../core/visbos-core/ui/forms/generic-modal/generic-modal.component';
import {AddressFormComponent} from '../../../../core/visbos-core/ui/forms/address-form/address-form.component';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {Invoice} from '../../../../shared/view-model/store-management/invoice.type';
import {appInjector} from '../../../../index';
import {AbstractVisbosControl} from '../../../../core/visbos-core/ui/forms/abstract-visbos-control';
import {MessageType} from '../../../../shared/redux-store/session';

@Component({
  selector: 'vb-invoice-view',
  templateUrl: './invoice-view.component.html',
  styleUrls: ['./invoice-view.component.css']
})
export class InvoiceViewComponent extends AbstractVisbosControl<Invoice, FormGroup> implements OnInit {

  @Input()
  invoice: Invoice;

  @ViewChild('addressModal')
  addressModal: GenericModalComponent;

  @ViewChild('addressForm')
  addressForm: AddressFormComponent;

  invoicePdfLink: string;

  private invoice$: ApolloQueryObservable<GetInvoiceQuery>;

  addressChangeType = 'billing_address';
  addressChangeTitle = "Rechnungsadresse ändern";


  constructor(sessionActions: SessionActions, private route: ActivatedRoute, fb: FormBuilder,
              private invoiceService: InvoiceService, private fileService: FileService, cd: ChangeDetectorRef) {

    super(fb, cd, sessionActions);

    this.actions = [
      {
        label: 'Änderungen speichern',
        icon: 'fa-save',
        action: () => this.saveChanges.apply(this),
        helpText: "Änderungen an der REchnung speichern",
      },
      {
        label: 'PDF generieren',
        icon: 'fa-file-pdf',
        action: () => this.generatePDF.apply(this),
        helpText: "PDF für die Rechnung generieren",
      }
    ];

  }

  ngOnInit() {

    super.ngOnInit();

    this.invoicePdfLink = null;

    this.dataSubscriptions.push(this.route.params.subscribe(params => {
        if (params.invoiceCuid) {
          this.loadInvoice(params.invoiceCuid);
        }
      })
    );
  }

  private loadInvoice(cuid: string) {

    if (!this.invoice$) {
      this.invoice$ = this.invoiceService.getInvoice(cuid);
    } else {
      this.invoice$.setVariables({cuid: cuid});
    }

    this.dataSubscriptions.push(this.invoice$.subscribe(res => {
      this.invoice = res.data.invoice;
      this.item = this.invoice;
      this.patchItemValues();
    }));

  }

  generatePDF() {

    if (!this.invoice) {
      return;
    }

    this.invoicePdfLink = undefined;

    this.dataSubscriptions.push(
      this.invoiceService.generateInvoicePdf(this.invoice.cuid).subscribe(
        res => {
          let data = this.fileService.base64toBlob(res.data.generateInvoicePdf, 'application/pdf', 512);
          this.fileService.saveAs(data, 'rechnung-' + this.invoice.invoice_no + '.pdf');
        }, err => {
          this.sessionActions.pushMessage("Fehler beim Generieren der PDF-Datei", MessageType.ERROR, "PDF-Erzeugung");
        })
    );
  }

  saveChanges() {
    this.invoiceService.updateInvoice({invoiceCuid: this.invoice.cuid, invoiceData: this.control.value})
      .subscribe(
        res => {
          this.sessionActions.pushMessage(`Rechnung ${res.data.updateInvoice.cuid} gespeichert`, MessageType.SUCCESS, 'Rechnung speichern');
        }, err => {
          this.sessionActions.pushMessage("Fehler beim Speichern der Rechnung", MessageType.ERROR, "Rechnung speichern");
        });
  }

  buildControlContent(): FormGroup {
    return InvoiceViewComponent.initControl(this.fb);
  }

  public static initControl(fb: FormBuilder = null): FormGroup {
    fb = fb ? fb : appInjector().get(FormBuilder);

    return fb.group({
      cuid: [null],

      invoice_address_name: [null, Validators.required],
      invoice_address_street: [null],
      invoice_address_city: [null],
      invoice_address_country: [null],

      billing_address_name: [null, Validators.required],
      billing_address_street: [null],
      billing_address_city: [null],
      billing_address_country: [null],

      invoice_no: [null],
      invoice_date: [null],
      delivery_date: [null],

      customer_number: [null],
      commission_number: [null],

      invoice_opening_text: [null],
      invoice_remarks: [null],
      invoice_payment_terms_text: [null],

      invoice_taxes_text: [null],
      invoice_ending_text: [null],

      administration_unit_id: [null, Validators.required]

    });
  }

  public setDefaultValues(item?: Invoice): { [controlKey: string]: any } | any {

    if (!item) {
      return undefined;
    }

    let ret = {
      cuid: this.setValue(item, 'cuid', AbstractVisbosControl.cuid()),

      invoice_address_name: this.setValue(item, 'invoice_address_name'),
      invoice_address_street: this.setValue(item, 'invoice_address_street'),
      invoice_address_city: this.setValue(item, 'invoice_address_city'),
      invoice_address_country: this.setValue(item, 'invoice_address_country'),

      billing_address_name: this.setValue(item, 'billing_address_name'),
      billing_address_street: this.setValue(item, 'billing_address_street'),
      billing_address_city: this.setValue(item, 'billing_address_city'),
      billing_address_country: this.setValue(item, 'billing_address_country'),

      invoice_no: this.setValue(item, 'invoice_no'),
      invoice_date: this.setValue(item, 'invoice_date'),
      delivery_date: this.setValue(item, 'delivery_date'),

      customer_number: this.setValue(item, 'customer_number'),
      commission_number: this.setValue(item, 'commission_number'),

      invoice_opening_text: this.setValue(item, 'invoice_opening_text'),
      invoice_remarks: this.setValue(item, 'invoice_remarks'),
      invoice_payment_terms_text: this.setValue(item, 'invoice_payment_terms_text'),

      invoice_taxes_text: this.setValue(item, 'invoice_taxes_text'),
      invoice_ending_text: this.setValue(item, 'invoice_ending_text'),

      administration_unit_id: this.setValue(item, 'administration_unit.cuid'),
    };

    return ret;
  };

  changeAddress(type) {
    this.addressChangeType = type;

    if (type == 'invoice_address') {
      this.addressChangeTitle = "Rechnungsaussteller-Adresse ändern";
    } else if (type == 'billing_address') {
      this.addressChangeTitle = "Rechnungsempfänger-Adresse ändern";
    }

    this.addressModal.show();

  }

}



// WEBPACK FOOTER //
// ./src/app/store-management/procurement/views/invoice-view/invoice-view.component.ts